<template>
  <div class="keyboard-shortcuts-panel">
    <ul class="keyboard-shortcuts-panel__list">
      <li
        v-for="shortcut in shortcuts"
        :key="shortcut.key"
        class="keyboard-shortcuts-panel__item"
      >
        <kbd class="keyboard-shortcuts-panel__key">{{ shortcut.key }}</kbd>
        <span class="keyboard-shortcuts-panel__label">{{ shortcut.label }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import { useVideoI18n } from '../../i18n'

interface Props {
  player: UseVideoPlayerReturn
}

const props = defineProps<Props>()
const { t } = useVideoI18n()

// A key that moves the playhead is listed only while the viewer may (`seeking`).
const shortcuts = computed(() =>
  [
    { key: 'Space', label: t('player.shortcuts.play_pause') },
    { key: 'K', label: t('player.shortcuts.play_pause') },
    { key: '\u2190', label: t('player.shortcuts.rewind_10_seconds'), seeks: true },
    { key: '\u2192', label: t('player.shortcuts.forward_10_seconds'), seeks: true },
    { key: 'J', label: t('player.shortcuts.rewind_10_seconds'), seeks: true },
    { key: 'L', label: t('player.shortcuts.forward_10_seconds'), seeks: true },
    { key: '\u2191', label: t('player.shortcuts.volume_up') },
    { key: '\u2193', label: t('player.shortcuts.volume_down') },
    { key: 'M', label: t('player.shortcuts.toggle_mute') },
    { key: 'F', label: t('player.shortcuts.toggle_fullscreen') },
    { key: 'Esc', label: t('player.shortcuts.exit_fullscreen') },
    {
      key: '0-9',
      label: t('player.shortcuts.go_to_0') + ' - ' + t('player.shortcuts.go_to_90'),
      seeks: true,
    },
    { key: '<', label: t('player.shortcuts.decrease_playback_speed') },
    { key: '>', label: t('player.shortcuts.increase_playback_speed') },
  ].filter((shortcut) => !shortcut.seeks || props.player.canSeek.value),
)
</script>

<style scoped lang="scss">
.keyboard-shortcuts-panel {
  padding: 8px 0;

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
  }

  &__key {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 28px;
    padding: 0 8px;
    background-color: var(--vp-surface-hover);
    border: 1px solid var(--vp-border-default);
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--vp-text-secondary);
    flex-shrink: 0;
  }

  &__label {
    font-size: 0.875rem;
    color: var(--vp-text-tertiary);
  }
}
</style>
